/**
 * Tema claro y oscuro.
 *
 * El valor efectivo lo fija un script en línea del `index.html` antes del
 * primer pintado; este módulo solo lo lee y lo cambia después. Mantener la
 * decisión inicial fuera del bundle es lo que evita el destello de tema
 * equivocado al abrir la app.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'paybae:theme'

/**
 * El tinte de la barra de estado, por tema.
 *
 * Son `--bg` de `tokens.css` copiados a mano, y tienen que seguir siéndolo: la
 * barra de estado la pinta el sistema operativo, no el CSS, así que aquí no
 * sirve una variable. Van los tres sitios juntos —este, el script en línea del
 * `index.html` y el manifiesto— porque si uno se queda atrás aparece una franja
 * de otro color justo sobre la app.
 */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#faf8f4',
  dark: '#090f1e',
}

export function getStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? getSystemTheme() : preference
}

export function getResolvedTheme(): ResolvedTheme {
  const attr = document.documentElement.dataset.theme
  return attr === 'dark' ? 'dark' : 'light'
}

export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  document.documentElement.dataset.theme = resolved

  // La barra de estado del sistema toma este color. Si no se actualiza, al
  // cambiar a oscuro queda una franja clara arriba que rompe la ilusión.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = THEME_COLOR[resolved]

  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Modo privado de Safari bloquea la escritura. El tema se aplica igual
    // durante la sesión; solo no se recuerda para la próxima.
  }

  return resolved
}

/* ── Paletas ─────────────────────────────────────────────────────────────── */

/**
 * El color de la app, independiente del claro u oscuro.
 *
 * Los valores viven en `tokens.css` (una regla por paleta y modo, con el
 * contraste de cada par ya medido); aquí solo está la lista que dibuja el
 * selector y la forma de aplicarla. Como el tema, la decisión inicial la toma
 * el script en línea del `index.html` antes del primer pintado: si la tomara el
 * bundle, la app abriría un fotograma en Tinta y luego cambiaría de color.
 */
export const PALETAS = [
  { id: 'tinta', nombre: 'Tinta' },
  { id: 'oceano', nombre: 'Océano' },
  { id: 'bosque', nombre: 'Bosque' },
  { id: 'zafiro', nombre: 'Zafiro' },
  // «Medianoche» no cabía en la columna de 60 px y se cortaba en «Medianoc…».
  { id: 'medianoche', nombre: 'Índigo' },
  { id: 'ciruela', nombre: 'Ciruela' },
  { id: 'frambuesa', nombre: 'Frambuesa' },
  { id: 'atardecer', nombre: 'Atardecer' },
  { id: 'terracota', nombre: 'Terracota' },
  { id: 'grafito', nombre: 'Grafito' },
] as const

export type PaletaId = (typeof PALETAS)[number]['id']

const PALETA_KEY = 'paybae:paleta'

function esPaleta(valor: string | null | undefined): valor is PaletaId {
  return PALETAS.some((p) => p.id === valor)
}

export function getPaleta(): PaletaId {
  const actual = document.documentElement.dataset.paleta
  return esPaleta(actual) ? actual : 'tinta'
}

export function applyPaleta(id: PaletaId): void {
  document.documentElement.dataset.paleta = id
  try {
    // Tinta es la de siempre: no se guarda, para que un cambio futuro de la
    // paleta por omisión les llegue a quienes nunca eligieron otra.
    if (id === 'tinta') localStorage.removeItem(PALETA_KEY)
    else localStorage.setItem(PALETA_KEY, id)
  } catch {
    // Igual que el tema: en modo privado se aplica, solo no se recuerda.
  }
}

/**
 * Sigue los cambios del sistema mientras la preferencia sea «automático».
 * Devuelve la función para dejar de escuchar.
 */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = (event: MediaQueryListEvent): void => {
    if (getStoredPreference() === 'system') {
      onChange(applyTheme('system'))
      void event
    }
  }
  media.addEventListener('change', handler)
  return () => media.removeEventListener('change', handler)
}
